import { useMemo, useEffect, useState, useRef } from 'react';

import { Box, Typography, useTheme, Tooltip } from '@material-ui/core';
import { FormProvider } from 'react-hook-form';
import { debounce, noop } from 'lodash';
import { LatLngLiteral, Map } from 'leaflet';
import { Polygon, Tooltip as LeafletTooltip } from 'react-leaflet';
import { BiAbacus } from 'react-icons/bi';

import { WidgetWrapper, WidgetWrapperProps } from '@vyce/core/src/components/Dashboard/WidgetWrapper';
import { AppIconButton } from '@vyce/core/src/components/AppIconButton';
import markerLight from '@vyce/core/src/assets/svg/marker.svg';
import markerWorker from '@vyce/core/src/assets/svg/marker-worker.svg';

import { useProximityToWorkWidgetData } from './hooks/useWidgetData';
import { Props } from '../QualificationWidget/types';
import { FilterDialog } from './components';
import { MapView } from '../../map/MapView';
import {
  convertPointToLatLng,
  convertStringPolygonToCoordinatesArray,
} from '../../../modules/timeModule/utils';
import { getPolygonCenter } from '../../../modules/timeModule/utils';
import { PageLoading } from '../../../components/PageLoading';
import { PieChart } from '../../../modules/widgetsModule/components';
import { LONDON_POSITION } from '../../map/constants';

const radii = [
  { miles: 1, color: '#610BEF' },
  { miles: 2, color: '#A996FF' },
  { miles: 3, color: '#BFBEFC' },
  { miles: 4, color: '#EBECFE' },
];

const BOROUGH_BOUNDARY_COLORS = [
  '#610BEF',
  '#0041AC',
  '#E91E63',
  '#FF9800',
  '#4CAF50',
  '#9C27B0',
  '#00BCD4',
  '#795548',
];

/* Proximity to work widget */
export const ProximityToWorkWidget = (props: Props & WidgetWrapperProps) => {
  const theme = useTheme();
  const {
    methods,
    filterMode,
    groupOneOptions: teamGroupOneOptions,
    groupTwoOptions: teamGroupTwoOptions,
    locationsWithGeo,
    selectedSite,
    proximityToWorkData,
    loading,
    teamId,
    isOpenFilterDialog,
    setFilterDialogOpen,
    setFilterDialogFalse,
    selectedTeamName,
    selectedLocationName,
    boroughs,
    selectedBoroughNames,
    selectedBoroughsWithBoundary,
    jobTitleOptions,
  } = useProximityToWorkWidgetData(props);
  const { showTeamsSelector = true } = props;

  const [fullscreenContainer, setFullscreenContainer] = useState<HTMLElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isBoroughMode = useMemo(() => {
    return filterMode === 'borough';
  }, [filterMode]);

  const isDistanceMode = useMemo(() => {
    return filterMode === 'distance';
  }, [filterMode]);

  useEffect(() => {
    const getFullscreenElement = () => {
      return (
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement ||
        null
      );
    };

    const updateContainer = () => {
      const fullscreenEl = getFullscreenElement();
      setFullscreenContainer(fullscreenEl as HTMLElement | null);
    };

    updateContainer();

    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange',
    ];
    events.forEach(event => {
      document.addEventListener(event, updateContainer);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateContainer);
      });
    };
  }, []);

  const menuProps = useMemo(() => {
    return fullscreenContainer ? { container: fullscreenContainer } : {};
  }, [fullscreenContainer]);

  const over45MinutesWorkersCount = useMemo(() => {
    return proximityToWorkData?.workers.filter(worker => worker.commute_time > 45).length || 0;
  }, [proximityToWorkData?.workers]);

  const markers = useMemo(() => {
    if (filterMode === 'borough') {
      return [];
    }
    const locationsToShow = selectedSite ? [selectedSite] : locationsWithGeo;
    return locationsToShow
      .map(location => {
        let latLng: LatLngLiteral | undefined = undefined;
        if (location.geo_point) {
          latLng = convertPointToLatLng(location.geo_point);
        } else if (location.geo_polygon) {
          const coordinates = convertStringPolygonToCoordinatesArray(location.geo_polygon);
          latLng = getPolygonCenter(coordinates);
        }
        if (!latLng) {
          return undefined;
        }
        return {
          lat: latLng.lat,
          lon: latLng.lng,
          name: location.name,
          id: location.uuid,
          picture: markerLight,
        };
      })
      .filter((marker): marker is NonNullable<typeof marker> => marker !== undefined);
  }, [filterMode, locationsWithGeo, selectedSite]);

  const workersMarkers = useMemo(() => {
    return (
      proximityToWorkData?.workers.map(worker => {
        return {
          lon: worker.geo_point ? convertPointToLatLng(worker.geo_point).lat : undefined,
          lat: worker.geo_point ? convertPointToLatLng(worker.geo_point).lng : undefined,
          picture: worker.photo || markerWorker,
          name: `${worker.name} - ${worker.distance?.toFixed(2)} miles`,
          id: worker.user_id,
          size: 20,
        };
      }) || []
    );
  }, [proximityToWorkData?.workers]);

  const center = useMemo(() => {
    if (filterMode === 'borough') {
      if (selectedBoroughsWithBoundary.length > 0) {
        const firstBorough = selectedBoroughsWithBoundary[0];
        const coords = convertStringPolygonToCoordinatesArray(firstBorough.boundary);
        const centerPoint = getPolygonCenter(coords);
        return { lat: centerPoint.lat, lng: centerPoint.lng };
      }
      return LONDON_POSITION;
    }
    if (selectedSite) {
      const marker = markers.find(marker => marker.id === selectedSite.uuid);
      if (marker) {
        return { lat: marker.lat, lng: marker.lon };
      }
      return undefined;
    }
    return markers.length > 0
      ? markers[0].lat && markers[0].lon
        ? { lat: markers[0].lat, lng: markers[0].lon }
        : undefined
      : undefined;
  }, [filterMode, selectedSite, markers, selectedBoroughsWithBoundary]);

  const getLabel = (key: string) => {
    switch (key) {
      case 'within_1_mile':
        return 'Within 1 mile';
      case 'within_1_2_miles':
        return 'Within 1-2 miles';
      case 'within_2_3_miles':
        return 'Within 2-3 miles';
      case 'within_3_4_miles':
        return 'Within 3-4 miles';
      case 'more_than_4_miles':
        return 'More than 4 miles';
      default:
        return key.replace(/_/g, ' ');
    }
  };

  const getColor = (key: string) => {
    switch (key) {
      case 'within_1_mile':
        return '#610BEF';
      case 'within_1_2_miles':
        return '#0041AC';
      case 'within_2_3_miles':
        return '#005BD4';
      case 'within_3_4_miles':
        return '#50C8FC';
      case 'more_than_4_miles':
        return '#8DE9FF';
      default:
        return '#610BEF';
    }
  };

  const boroughPolygons = useMemo(() => {
    return selectedBoroughsWithBoundary.map((borough, index) => {
      const coords = convertStringPolygonToCoordinatesArray(borough.boundary);
      const positions = coords.map(c => [c[1], c[0]] as [number, number]);
      return {
        name: borough.name,
        positions,
        color: BOROUGH_BOUNDARY_COLORS[index % BOROUGH_BOUNDARY_COLORS.length],
      };
    });
  }, [selectedBoroughsWithBoundary]);

  const pieChartData = useMemo(() => {
    if (filterMode === 'borough') {
      const boroughStats = proximityToWorkData?.borough_stats || {};
      return Object.entries(boroughStats)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([boroughName, count], index) => ({
          id: boroughName,
          label: boroughName,
          value: count,
          color: BOROUGH_BOUNDARY_COLORS[index % BOROUGH_BOUNDARY_COLORS.length],
        }));
    }
    return Object.entries(proximityToWorkData?.distance_ranges || {}).map(([key, value]) => ({
      id: key,
      label: getLabel(key),
      value: value,
      color: getColor(key),
    }));
  }, [filterMode, proximityToWorkData?.distance_ranges, proximityToWorkData?.borough_stats]);

  useEffect(() => {
    const container = containerRef.current;

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        if (mapRef?.current) {
          mapRef.current.invalidateSize();
        }
      }, 100)
    );

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, []);

  return (
    <WidgetWrapper
      {...props}
      renderExtraControls={() => (
        <Tooltip title="Filter">
          <AppIconButton variant="paper" isSmall isBorder onClick={setFilterDialogOpen}>
            <BiAbacus size="20px" color={theme.palette.text.primary} />
          </AppIconButton>
        </Tooltip>
      )}
    >
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gridGap: 12 }}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(noop)}
            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
          >
            <Box width="100%">
              <Typography variant="body1" color="textPrimary" component="span" display="block">
                {isDistanceMode ? 'Distance to work' : 'Workers by borough'}
                {': '}
                <Box component="strong" onClick={setFilterDialogOpen} style={{ cursor: 'pointer' }}>
                  {showTeamsSelector ? selectedTeamName || '—' : 'selected'}
                </Box>
                {isDistanceMode && (
                  <>
                    {' at '}
                    <Box component="strong" onClick={setFilterDialogOpen} style={{ cursor: 'pointer' }}>
                      {selectedLocationName || '—'}
                    </Box>
                  </>
                )}
                {isBoroughMode && selectedBoroughNames && (
                  <>
                    {' in '}
                    <Box component="strong" onClick={setFilterDialogOpen} style={{ cursor: 'pointer' }}>
                      {selectedBoroughNames}
                    </Box>
                  </>
                )}
              </Typography>
            </Box>

            <FilterDialog
              isOpen={isOpenFilterDialog}
              onClose={setFilterDialogFalse}
              onUpdate={noop}
              methods={methods}
              groupOneOptions={teamGroupOneOptions}
              groupTwoOptions={teamGroupTwoOptions}
              locationOptions={locationsWithGeo.map(location => ({
                name: location.name,
                value: location.uuid,
              }))}
              boroughs={boroughs}
              jobTitleOptions={jobTitleOptions}
              menuProps={menuProps}
              teamSelectDisabled={!!teamId}
              dialogContainer={fullscreenContainer}
            />

            {markers.length > 0 ||
            (isBoroughMode &&
              (selectedBoroughsWithBoundary.length > 0 ||
                (proximityToWorkData?.workers?.length ?? 0) > 0)) ? (
              <Box display="flex" gridGap={12} marginTop={3} flex={1} minHeight={0}>
                <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
                  <MapView
                    height="100%"
                    centerPosition={center}
                    markers={[...markers, ...workersMarkers]}
                    onMarkerClick={noop}
                    clustered={false}
                    radii={isDistanceMode ? radii : undefined}
                    zoom={12}
                    whenCreated={map => {
                      setTimeout(() => map.invalidateSize(), 100);
                      mapRef.current = map;
                    }}
                  >
                    <>
                      {isBoroughMode &&
                        boroughPolygons.map((poly, i) => (
                          <Polygon
                            key={i}
                            positions={poly.positions}
                            pathOptions={{
                              color: poly.color,
                              fillColor: poly.color,
                              fillOpacity: 0.15,
                              weight: 2,
                              opacity: 0.8,
                            }}
                          >
                            <LeafletTooltip permanent={false} direction="top">
                              {poly.name}
                            </LeafletTooltip>
                          </Polygon>
                        ))}
                    </>
                  </MapView>

                  {isDistanceMode && (
                    <>
                      <Typography style={{ marginTop: 12 }} color="textSecondary" variant="body2">
                        The average distance: {proximityToWorkData?.average_distance || '-'} miles
                      </Typography>
                      <Typography style={{ marginTop: 8 }} color="textSecondary" variant="body2">
                        Workers commuting over 45 min: {over45MinutesWorkersCount}
                      </Typography>
                    </>
                  )}
                </Box>

                <Box flex={1}>
                  <Box height="100%" width="100%" display="flex" flexDirection="column" alignItems="center">
                    {loading ? (
                      <PageLoading />
                    ) : (
                      <PieChart
                        data={pieChartData}
                        withPercentage={false}
                        handleChartClick={noop}
                        sortLegendData={false}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
                <Typography color="textSecondary" variant="body2">
                  No location found
                </Typography>
              </Box>
            )}
          </form>
        </FormProvider>
      </div>
    </WidgetWrapper>
  );
};
